import { cn } from '@/shared/lib/cn';
import { KeyRound, Mail } from 'lucide-react';
import { type SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/Button';
import { PasswordField } from '@/shared/components/PasswordField';
import { TextField } from '@/shared/components/TextField';
import { apolloClient, repointApolloLink } from '@/app/providers/apollo';
import { INVALID_CREDENTIALS } from '@/app/auth/services/auth.service';
import { fetchAccountStatus, type AccountStatus } from '@/app/auth/services/code-flow.service';
import { discoverBackendForEmail } from '@/shared/lib/backend-discovery';
import { useAuthStore } from '@/app/auth/stores/auth.store';
import { formatError } from '@/shared/lib/format-error';
import { useLogin } from '../useLogin';

interface LoginFormProps {
  email: string;
  mode: 'email' | 'password';
  onEmailChange: (email: string) => void;
  onForgotPassword: () => void;
  onPasswordChange: (password: string) => void;
  onStatus: (email: string, status: AccountStatus) => void;
  password: string;
}

export const LoginForm = ({
  email,
  mode,
  onEmailChange,
  onForgotPassword,
  onPasswordChange,
  onStatus,
  password,
}: LoginFormProps) => {
  const { t } = useTranslation('app');
  const [isChecking, setIsChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  const loginMutation = useLogin();
  const passwordInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mode === 'password') {
      passwordInputRef.current?.focus();
    }
  }, [mode]);

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    void (async () => {
      if (await discoverBackendForEmail(email)) {
        repointApolloLink();
        useAuthStore.getState().clearSession();
        await apolloClient.clearStore();
      }

      if (mode === 'password') {
        loginMutation.mutate({ email, password });
        return;
      }

      setIsChecking(true);
      setCheckFailed(false);

      const status = await fetchAccountStatus(email);

      setIsChecking(false);
      if (status === null) {
        setCheckFailed(true);
        return;
      }
      onStatus(email, status);
    })();
  };

  return (
    <form className="grid gap-1 max-w-md mx-auto" onSubmit={handleSubmit}>
      <TextField
        label={t('login.email')}
        icon={<Mail aria-hidden size={18} />}
        value={email}
        onChange={onEmailChange}
        autoComplete="email"
        required
        type="email"
      />

      <PasswordField
        label={t('login.password')}
        className={cn(mode === 'password' ? 'mt-4' : 'sr-only')}
        value={password}
        onChange={onPasswordChange}
        autoComplete="current-password"
        ref={passwordInputRef}
        required={mode === 'password'}
        tabIndex={mode === 'password' ? 0 : -1}
      />

      {mode === 'password' && loginMutation.error ? (
        <p className="notice danger">
          {loginMutation.error.message === INVALID_CREDENTIALS
            ? t('login.invalidCredentials')
            : formatError(loginMutation.error)}
        </p>
      ) : null}

      <Button
        className="mt-6"
        color="primary"
        disabled={mode === 'email' ? isChecking : loginMutation.isPending}
        fullWidth
        type="submit"
      >
        {mode === 'password'
          ? loginMutation.isPending
            ? t('login.submitting')
            : t('login.submit')
          : isChecking
            ? t('login.checking')
            : t('login.continue')}
      </Button>

      {mode === 'password' ? (
        <Button
          className="mt-2"
          color="neutral"
          fullWidth
          leadingIcon={<KeyRound aria-hidden size={14} />}
          onClick={onForgotPassword}
          variant="ghost"
        >
          {t('login.forgotPassword')}
        </Button>
      ) : null}

      {mode === 'email' && checkFailed ? (
        <p className="notice danger">{t('login.statusFailed')}</p>
      ) : null}
    </form>
  );
};
